import { useState, useRef, useEffect, useCallback } from 'react';
import { Camera, AlertCircle, Loader2, RotateCcw, X } from 'lucide-react';
import { toast } from 'sonner';
import useIsMounted from '../../../../hooks/useIsMounted';

const WebcamCapture = ({ overlayType = 'circle', onCapture, onCancel, guideText }) => {
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [cameraError, setCameraError] = useState(null);
  const [capturedPreview, setCapturedPreview] = useState(null);
  const [stream, setStream] = useState(null);
  
  const videoRef = useRef(null);
  const activeStreamRef = useRef(null);
  const isMounted = useIsMounted();

  const startCamera = async () => {
    setCameraError(null);
    setCapturedPreview(null);
    setIsCameraActive(false);
    setStream(null);

    try {
      const constraints = {
        video: {
          facingMode: overlayType === 'circle' ? 'user' : 'environment',
          width: { ideal: overlayType === 'circle' ? 640 : 1280 },
          height: { ideal: overlayType === 'circle' ? 480 : 720 }
        }
      };

      const mediaStream = await navigator.mediaDevices.getUserMedia(constraints);
      
      if (!isMounted.current) {
        mediaStream.getTracks().forEach((track) => track.stop());
        return;
      }

      activeStreamRef.current = mediaStream;
      setStream(mediaStream);
      setIsCameraActive(true);
    } catch (err) {
      console.error('Error accessing webcam:', err);
      // If environment camera fails for card capture, try user facing camera
      if (overlayType === 'rectangle') {
        try {
          const fallbackStream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: 'user', width: { ideal: 640 }, height: { ideal: 480 } }
          });
          
          if (!isMounted.current) {
            fallbackStream.getTracks().forEach((track) => track.stop());
            return;
          }

          activeStreamRef.current = fallbackStream;
          setStream(fallbackStream);
          setIsCameraActive(true);
          return;
        } catch (fallbackErr) {
          console.error('Fallback webcam failed:', fallbackErr);
        }
      }
      if (isMounted.current) {
        setCameraError('Could not access webcam. Please verify camera permissions are granted.');
        toast.error('Webcam access denied.');
      }
    }
  };

  const stopCamera = () => {
    if (activeStreamRef.current) {
      activeStreamRef.current.getTracks().forEach((track) => track.stop());
      activeStreamRef.current = null;
    }
    setStream(null);
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };

  useEffect(() => {
    startCamera();
    return () => {
      stopCamera();
    };
  }, [overlayType]);

  const videoCallbackRef = useCallback((node) => {
    if (node && stream) {
      node.srcObject = stream;
      node.play().catch(e => console.error("Error playing video:", e));
    }
    videoRef.current = node;
  }, [stream]);

  const handleCapture = () => {
    if (videoRef.current) {
      const video = videoRef.current;
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth || 640;
      canvas.height = video.videoHeight || 480;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      
      canvas.toBlob((blob) => {
        const file = new File([blob], 'capture.jpg', { type: 'image/jpeg' });
        setCapturedPreview(URL.createObjectURL(blob));
        stopCamera();
        setIsCameraActive(false);
        onCapture({ file, blob });
      }, 'image/jpeg', 0.95);
    }
  };

  const handleRetake = () => {
    setCapturedPreview(null);
    startCamera();
  };

  return (
    <div className="space-y-4">
      {cameraError ? (
        <div className="text-center py-8 text-error">
          <AlertCircle className="w-12 h-12 mx-auto mb-2 text-error" />
          <p className="font-semibold">{cameraError}</p>
          {onCancel && (
            <button type="button" onClick={onCancel} className="btn btn-sm btn-outline mt-4">
              Go Back
            </button>
          )}
        </div>
      ) : (
        <div className="relative rounded-2xl overflow-hidden bg-black aspect-video border border-base-300 shadow-xl flex items-center justify-center">
          
          {/* Loading Stream */}
          {!isCameraActive && !capturedPreview && (
            <div className="text-white flex flex-col items-center gap-2">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-sm">Initializing camera...</p>
            </div>
          )}

          {/* Live Stream View */}
          {isCameraActive && !capturedPreview && (
            <>
              <video 
                ref={videoCallbackRef} 
                playsInline 
                muted 
                className={`w-full h-full object-cover ${overlayType === 'circle' ? 'scale-x-[-1]' : ''}`}
              />

              {/* Overlay Guidelines */}
              {overlayType === 'circle' ? (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-56 h-56 md:w-64 md:h-64 rounded-full border-4 border-primary border-dashed shadow-[0_0_0_9999px_rgba(0,0,0,0.6)] flex items-center justify-center relative">
                    <div className="absolute inset-[-4px] rounded-full border-2 border-primary animate-ping opacity-25" />
                    <span className="absolute bottom-[-45px] text-xs bg-black/85 px-4 py-1.5 rounded-full text-white font-medium shadow-md whitespace-nowrap">
                      {guideText || 'Align your face inside the frame'}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="absolute inset-0 border-[30px] sm:border-[40px] border-black/60 flex items-center justify-center">
                  <div className="w-[85%] h-[75%] border-2 border-dashed border-white rounded-xl shadow-[0_0_0_9999px_rgba(0,0,0,0.5)] flex items-center justify-center relative">
                    <span className="absolute bottom-3 text-xs bg-black/75 px-3 py-1 rounded-full text-white font-medium text-center max-w-[90%]">
                      {guideText || 'Align your document card here'}
                    </span>
                  </div>
                </div>
              )}

              {/* Trigger Button */}
              <button 
                type="button"
                onClick={handleCapture}
                className="absolute bottom-6 btn btn-circle btn-primary btn-lg shadow-2xl border-2 border-white transform hover:scale-105 transition-all"
              >
                <Camera className="w-6 h-6" />
              </button>
            </>
          )}

          {/* Captured Preview View */}
          {capturedPreview && (
            <div className="relative w-full h-full flex items-center justify-center bg-base-200">
              <img 
                src={capturedPreview} 
                alt="Captured Snapshot" 
                className={`w-full h-full object-cover ${overlayType === 'circle' ? 'scale-x-[-1]' : ''}`}
              />
              <button 
                type="button"
                onClick={handleRetake}
                className="absolute bottom-6 btn btn-sm btn-outline gap-2 bg-base-100/90 shadow-md hover:bg-base-100"
              >
                <RotateCcw className="w-4 h-4" />
                Retake Photo
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WebcamCapture;
